import * as Actions from '@/redux/actions';
import { faCircleXmark } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CircularProgress, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Button } from 'components/button';
import { useDispatch, useLocale, useSelector } from 'hooks';
import { useI18n } from 'locales';
import get from 'lodash/get';
import { Suspense, useEffect } from 'react';
import { Trans } from 'react-i18next';
import { shallowEqual } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import thousands from 'thousands';
import Heading from '../components/Heading';

const selector = (state) => ({
  publicGivingInfo: state.publicGivingInfo,
});

function EntryFailContent() {
  const { publicGivingInfo } = useSelector(selector, shallowEqual);
  const dispatch = useDispatch();
  const t = useI18n();
  const [searchParams] = useSearchParams();
  const locale = useLocale();
  const givingId = searchParams.get('id');
  const giving = get(publicGivingInfo, 'data', {});

  useEffect(() => {
    if (givingId) {
      dispatch(Actions.readPublicGiving({ payload: { id: givingId } }));
    }
  }, [dispatch, givingId]);

  useEffect(() => {
    document.title = 'Do Not Require a Receipt – St Andrews Church Kowloon';
  }, []);

  const isLoading = publicGivingInfo.isFetching;

  if (isLoading && !publicGivingInfo.dataFetched) {
    return (
      <Stack sx={{
        backgroundColor: 'background.default',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
      }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack sx={{
      backgroundColor: 'background.default',
      alignItems: 'center',
      pb: 8,
    }}>
      <Heading />

      <Grid gap={2} sx={{ flexDirection: 'column', display: 'flex', alignItems: 'center', px: 2, py: 3 }}>
        <Typography color='error.main' sx={{ fontSize: 120 }}>
          <FontAwesomeIcon icon={faCircleXmark} />
        </Typography>

        <Typography variant='h4' color='secondary' sx={{ textAlign: 'center', fontWeight: 'bold' }}>
          {t('PAYMENT FAILED')}
        </Typography>

        <Stack gap={0.5}>
          <Typography variant='body1' color='info.700' sx={{ textAlign: 'center' }}>
            {t('Unfortunately, your payment for the giving to ')}{get(giving, 'fund.nameEng', '[Fund Name]')}{t(' could not be processed.')}
          </Typography>
          <Typography variant='body1' color='info.700' sx={{ textAlign: 'center' }}>
            {t('Amount: HKD ')}{thousands((giving.amount || 0).toFixed(2))}
          </Typography>
          <Typography variant='body1' color='info.700' sx={{ textAlign: 'center' }}>
            {t('Please check your payment details and try again.')}
          </Typography>
        </Stack>

        <Stack gap={2} sx={{ mt: 3, alignItems: 'center' }}>
          <a href={`/giving/do-not-require-a-receipt?locale=${locale}`} style={{ textDecoration: 'none' }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1rem',
                px: 4,
                py: 1.5,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'primary.dark'
                }
              }}
            >
              {t('TRY AGAIN')}
            </Button>
          </a>

          <Typography variant='body2' color='info.700' sx={{ textAlign: 'center' }}>
            <Trans
              i18nKey="Need help?"
              components={{ hyperlink: <a href="mailto:info@standrews.org.hk" /> }}
            />
          </Typography>
        </Stack >
      </Grid >

    </Stack >
  );
}

function EntryFailPage() {
  return (
    <Suspense fallback={
      <Stack sx={{
        backgroundColor: 'background.default',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
      }}>
        <CircularProgress />
      </Stack>
    }>
      <EntryFailContent />
    </Suspense>
  );
}

export default EntryFailPage;
